const PushNotificationsHook = {
    mounted() {
        this.vapidPublicKey = this.el.dataset.vapidPublicKey;
        this.dismissKey = this.el.dataset.dismissKey;
        this.role = this.el.dataset.role || 'banner'; // 'banner' or 'settings'
        this.handleWindowFocus = () => this.checkPermission();

        this.checkPermission();

        // Setup event listeners for enable buttons within the container
        this.el.querySelectorAll("[data-action='enable-push']").forEach(btn => {
            btn.addEventListener("click", () => this.handleEnableClick());
        });

        // Setup dismissal (only for banner role usually)
        this.el.querySelectorAll("[data-action='dismiss']").forEach(btn => {
            btn.addEventListener("click", () => this.dismiss());
        });

        // Auto-register SW and re-subscribe when permission is already granted.
        // This handles the case where the user previously granted permission but
        // the subscription was lost (deploy, browser update, cache clear, etc.)
        if (this.notificationPermission() === "granted") {
            this.ensureSubscription();
        }

        window.addEventListener("focus", this.handleWindowFocus);
    },

    destroyed() {
        if (this.handleWindowFocus) {
            window.removeEventListener("focus", this.handleWindowFocus);
        }
    },

    supportsNotifications() {
        return typeof window !== "undefined" && "Notification" in window;
    },

    supportsPushMessaging() {
        return this.supportsNotifications() &&
            typeof navigator !== "undefined" &&
            "serviceWorker" in navigator &&
            "PushManager" in window;
    },

    notificationPermission() {
        if (!this.supportsNotifications()) {
            return "unsupported";
        }

        return window.Notification.permission;
    },

    checkPermission() {
        if (!this.supportsPushMessaging()) {
            this.setPermissionState("unsupported");
            return;
        }

        const permission = this.notificationPermission();

        if (permission === "granted") {
            this.setPermissionState("granted");
        } else if (permission === "denied") {
            this.setPermissionState("denied");
        } else {
            // Default capability
            if (this.isDismissed()) {
                this.setPermissionState("dismissed");
            } else {
                this.setPermissionState("default");
            }
        }
    },

    setPermissionState(state) {
        // Remove all state classes
        this.el.classList.remove("permission-granted", "permission-denied", "permission-default", "permission-unsupported", "permission-dismissed");

        // Add current state class
        this.el.classList.add(`permission-${state}`);

        // Update dataset for styling hooks
        this.el.dataset.permissionState = state;

        // Banner visibility logic:
        // If role is banner, we only show if state is 'default'.
        if (this.role === 'banner') {
            if (state === 'default') {
                this.el.classList.remove("hidden");
            } else {
                this.el.classList.add("hidden");
            }
        }
    },

    isDismissed() {
        if (!this.dismissKey) return false;

        try {
            return localStorage.getItem(`push_dismissed_${this.dismissKey}`) === "true";
        } catch (_error) {
            return false;
        }
    },

    dismiss() {
        if (this.dismissKey) {
            try {
                localStorage.setItem(`push_dismissed_${this.dismissKey}`, "true");
            } catch (_error) {
                // Ignore storage failures and still hide the banner for this page view.
            }
        }
        this.setPermissionState("dismissed");
    },

    async handleEnableClick() {
        const permission = await this.ensurePermission();
        if (permission === "granted") {
            await this.subscribeForPush();
            this.checkPermission();
        } else {
            this.checkPermission();
        }
    },

    async ensurePermission() {
        if (!this.vapidPublicKey || this.vapidPublicKey.length < 40) {
            console.warn("[PushNotifications] Disabled: VAPID key missing or invalid");
            return "unsupported";
        }

        if (!this.supportsNotifications()) {
            return "unsupported";
        }

        if (this.notificationPermission() === "granted") {
            return "granted";
        }

        const permission = await window.Notification.requestPermission();
        return permission;
    },

    /**
     * Ensures the service worker is registered and has an active push subscription.
     * Called automatically on mount when permission is already granted, so that
     * subscriptions survive deploys, browser updates, and cache clears.
     */
    async ensureSubscription() {
        if (!this.vapidPublicKey || this.vapidPublicKey.length < 40 || !this.supportsPushMessaging()) {
            return;
        }

        try {
            const registration = await navigator.serviceWorker.register("/sw.js");

            // Wait for the service worker to be ready
            await navigator.serviceWorker.ready;

            const existing = await registration.pushManager.getSubscription();
            if (existing) {
                // Subscription already exists — re-send to server in case it was lost
                this.sendSubscriptionToServer(existing);
                return;
            }

            // No subscription — create one
            await this.subscribeForPush();
        } catch (error) {
            console.error("[PushNotifications] Auto-registration failed:", error);
        }
    },

    async subscribeForPush() {
        if (!this.supportsPushMessaging()) {
            this.checkPermission();
            return;
        }

        try {
            const registration = await navigator.serviceWorker.register("/sw.js");

            // Wait for the SW to be active before subscribing
            await navigator.serviceWorker.ready;

            const existing = await registration.pushManager.getSubscription();
            if (existing) {
                this.sendSubscriptionToServer(existing);
                return;
            }

            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey)
            });

            this.sendSubscriptionToServer(subscription);

        } catch (error) {
            console.error("[PushNotifications] Subscription failed:", error);

            // Surface the error to the user via a flash message
            this.pushEvent("push_subscription_error", {
                reason: error.message || "Unknown error"
            });
        }
    },

    sendSubscriptionToServer(subscription) {
        const p256dh = subscription.getKey("p256dh");
        const auth = subscription.getKey("auth");

        this.pushEvent("store_subscription", {
            endpoint: subscription.endpoint,
            keys: {
                p256dh: p256dh ? this.arrayBufferToBase64(p256dh) : null,
                auth: auth ? this.arrayBufferToBase64(auth) : null
            }
        });
    },

    urlBase64ToUint8Array(base64String) {
        const padding = "=".repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/\-/g, "+").replace(/_/g, "/");
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    },

    arrayBufferToBase64(buffer) {
        var binary = '';
        var bytes = new Uint8Array(buffer);
        var len = bytes.byteLength;
        for (var i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    }
};

export default PushNotificationsHook;
